import { useQuery } from '@tanstack/react-query';
import { client } from '../api/client';
import type { CustomField, CustomValues, Page } from '../types';

/**
 * The custom fields a super admin added, rendered onto a form.
 *
 * One component for both entities, because the difference is a query
 * parameter. A second copy would be a second place for a field type to be
 * added and only half-supported.
 *
 * Values are keyed by field id throughout — never by label. A super admin
 * renaming "Shift preference" to "Shift" must relabel the box and leave every
 * answer where it is.
 *
 * `spellCheck` follows the rule the rest of the product uses: on for anything
 * a person writes in prose, off for a value from a list. A custom text field
 * could be either, and prose is the commoner case.
 */
export function useCustomFields(entity: 'candidate' | 'employee') {
  return useQuery<Page<CustomField>>({
    queryKey: ['custom-fields', entity],
    queryFn: async () =>
      (
        await client.get('/api/v1/custom-fields', {
          params: { entity, limit: 100 },
        })
      ).data,
  });
}

export default function CustomFieldInputs({
  fields,
  values,
  onChange,
  idPrefix,
}: {
  fields: CustomField[];
  values: CustomValues;
  onChange: (next: CustomValues) => void;
  /** Keeps label/input ids unique when two forms are open at once. */
  idPrefix: string;
}) {
  if (fields.length === 0) return null;

  const set = (id: string, raw: string) =>
    onChange({ ...values, [id]: raw === '' ? null : raw });

  return (
    <>
      {fields.map((field) => {
        const id = `${idPrefix}-${field.id}`;
        const current = values[field.id];
        const value = current == null ? '' : String(current);

        return (
          <div key={field.id}>
            <label className="label" htmlFor={id}>
              {field.label}
              {field.isRequired && <span className="text-red-500"> *</span>}
            </label>

            {field.fieldType === 'dropdown' ? (
              <select
                id={id}
                className="input"
                value={value}
                onChange={(e) => set(field.id, e.target.value)}
              >
                {/* Not preselected. Guessing which option somebody meant is how
                    a wrong value gets saved by doing nothing. */}
                <option value="">Not set</option>
                {(field.options ?? []).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={id}
                className="input"
                type={
                  field.fieldType === 'date'
                    ? 'date'
                    : field.fieldType === 'number'
                      ? 'number'
                      : 'text'
                }
                // The server enforces this too; the cap here is so the box
                // stops accepting rather than the save failing afterwards.
                maxLength={field.fieldType === 'text' ? 500 : undefined}
                spellCheck={field.fieldType === 'text'}
                value={value}
                onChange={(e) => set(field.id, e.target.value)}
              />
            )}
          </div>
        );
      })}
    </>
  );
}
